import { useState, useCallback, useRef } from 'react';

const usePageOperations = (initialPageCount) => {
  const [state, setState] = useState({
    pages: Array.from({ length: initialPageCount }, (_, i) => ({
      originalIndex: i,
      rotation: 0,
      deleted: false,
      annotation: null,
    })),
    splits: new Set(),
    selectedPageIndex: 0,
  });

  const historyRef = useRef([]);
  const futureRef = useRef([]);

  const pushHistory = useCallback(() => {
    historyRef.current.push(JSON.parse(JSON.stringify(state)));
    futureRef.current = [];
    if (historyRef.current.length > 50) {
      historyRef.current.shift();
    }
  }, [state]);

  const undo = useCallback(() => {
    if (historyRef.current.length === 0) return false;
    futureRef.current.push(JSON.parse(JSON.stringify(state)));
    const prevState = historyRef.current.pop();
    setState({
      ...prevState,
      splits: new Set(prevState.splits),
    });
    return true;
  }, [state]);

  const redo = useCallback(() => {
    if (futureRef.current.length === 0) return false;
    historyRef.current.push(JSON.parse(JSON.stringify(state)));
    const nextState = futureRef.current.pop();
    setState({
      ...nextState,
      splits: new Set(nextState.splits),
    });
    return true;
  }, [state]);

  const rotatePage = useCallback((pageIndex) => {
    pushHistory();
    setState((prev) => ({
      ...prev,
      pages: prev.pages.map((page, idx) =>
        idx === pageIndex
          ? { ...page, rotation: (page.rotation + 90) % 360 }
          : page
      ),
    }));
  }, [pushHistory]);

  const deletePage = useCallback((pageIndex) => {
    pushHistory();
    setState((prev) => ({
      ...prev,
      pages: prev.pages.map((page, idx) =>
        idx === pageIndex ? { ...page, deleted: true } : page
      ),
    }));
  }, [pushHistory]);

  const undeletePage = useCallback((pageIndex) => {
    pushHistory();
    setState((prev) => ({
      ...prev,
      pages: prev.pages.map((page, idx) =>
        idx === pageIndex ? { ...page, deleted: false } : page
      ),
    }));
  }, [pushHistory]);

  const toggleSplit = useCallback((beforePageIndex) => {
    pushHistory();
    setState((prev) => {
      const newSplits = new Set(prev.splits);
      if (newSplits.has(beforePageIndex)) {
        newSplits.delete(beforePageIndex);
      } else {
        newSplits.add(beforePageIndex);
      }
      return { ...prev, splits: newSplits };
    });
  }, [pushHistory]);

  const reorderPages = useCallback((fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    pushHistory();
    setState((prev) => {
      const newPages = [...prev.pages];
      const [movedPage] = newPages.splice(fromIndex, 1);
      newPages.splice(toIndex, 0, movedPage);
      return { ...prev, pages: newPages };
    });
  }, [pushHistory]);

  const setSelectedPage = useCallback((pageIndex) => {
    setState((prev) => ({ ...prev, selectedPageIndex: pageIndex }));
  }, []);

  const annotatePage = useCallback((pageIndex, annotation) => {
    pushHistory();
    setState((prev) => ({
      ...prev,
      pages: prev.pages.map((page, idx) =>
        idx === pageIndex ? { ...page, annotation } : page
      ),
    }));
  }, [pushHistory]);

  const appendPages = useCallback((newPages) => {
    pushHistory();
    setState((prev) => ({
      ...prev,
      pages: [...prev.pages, ...newPages],
    }));
  }, [pushHistory]);

  const buildOperations = useCallback(() => {
    const operations = [];
    const { pages, splits } = state;

    pages.forEach((page, idx) => {
      if (page.rotation !== 0) {
        operations.push({
          type: 'rotate',
          pageIndex: page.originalIndex,
          degrees: page.rotation,
        });
      }
      if (page.deleted) {
        operations.push({
          type: 'delete',
          pageIndex: page.originalIndex,
        });
      }
      if (page.originalIndex !== idx) {
        operations.push({
          type: 'reorder',
          fromIndex: page.originalIndex,
          toIndex: idx,
        });
      }
    });

    Array.from(splits).forEach((beforePageIndex) => {
      operations.push({
        type: 'split',
        beforePageIndex,
      });
    });

    return operations;
  }, [state]);

  const isDirty = useCallback(() => {
    const { pages, splits } = state;
    if (splits.size > 0) return true;
    return pages.some(
      (page, idx) =>
        page.rotation !== 0 ||
        page.deleted ||
        page.originalIndex !== idx ||
        page.annotation !== null
    );
  }, [state]);

  return {
    pages: state.pages,
    splits: state.splits,
    selectedPageIndex: state.selectedPageIndex,
    rotatePage,
    deletePage,
    undeletePage,
    toggleSplit,
    reorderPages,
    setSelectedPage,
    annotatePage,
    appendPages,
    undo,
    redo,
    canUndo: historyRef.current.length > 0,
    canRedo: futureRef.current.length > 0,
    buildOperations,
    isDirty: isDirty(),
  };
};

export default usePageOperations;
